import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { logout } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import { stripeApi, userApi } from '../generated/clients';
import { AccountType } from '@/models/user';
import { ResponseError } from '@/generated';
import { DefaultContainer } from '@/components/DefaultContainer';
import { AppContext } from '@/contexts/app.context';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Delete } from '@mui/icons-material';

export const MyAccount = () => {
  const context = useContext(AppContext);
  const user = context.state?.userProfile;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [changeUsernameDialogOpen, setChangeUsernameDialogOpen] = useState(false);
  const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = useState(false);

  return (
    <DefaultContainer>
      <DeleteAccountDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} />
      <ChangeUsernameDialog open={changeUsernameDialogOpen} onClose={() => setChangeUsernameDialogOpen(false)} />
      <CancelSubscriptionDialog open={cancelSubscriptionDialogOpen} onClose={() => setCancelSubscriptionDialogOpen(false)} />
      <Paper sx={{ m: 2 }} elevation={3}>
        <Box sx={{ p: 2 }}>
          <Stack gap={2} sx={{ my: 3, alignItems: 'stretch' }}>
            <Grid container sx={{ minHeight: 50, alignItems: 'center', gap: 2 }} direction={'row'}>
              <PersonIcon />
              <Typography>{user?.username}</Typography>
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setChangeUsernameDialogOpen(true)}>
                Change Username
              </Button>
            </Grid>
            <Stack sx={{ minHeight: 50, alignItems: 'center', gap: 2 }} direction={'row'}>
              <EmailIcon /> <Typography>{user?.email}</Typography>
            </Stack>
            <Grid container sx={{ minHeight: 50, alignItems: 'center', gap: 2, alignContent: 'center', textAlign: 'center' }} direction={'row'}>
              <AccountCircleIcon />
              <Typography>{user?.accountType === AccountType.basic ? 'Basic Account' : 'Premium Account'}</Typography>

              {/* {user?.accountType === AccountType.basic && (
                <Button
                  startIcon={<UpgradeIcon />}
                  variant="outlined"
                  onClick={async () => {
                    const resp = await stripeApi.stripeCreateSubscription({});
                    window.location.href = resp.redirectUrl;
                    context.refreshUserProfile();
                  }}
                >
                  Upgrade to premium
                </Button>
              )}
              {user?.accountType === AccountType.premium && (
                <Button
                  variant="outlined"
                  color={'warning'}
                  onClick={async () => {
                    setCancelSubscriptionDialogOpen(true);
                  }}
                >
                  Cancel your premium subscription
                </Button>
              )} */}
              <Button startIcon={<Delete />} variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)} sx={{ m: 1 }}>
                Delete your Account
              </Button>
            </Grid>
          </Stack>
        </Box>
      </Paper>
    </DefaultContainer>
  );
};

const ChangeUsernameDialog = (props: { open: boolean; onClose: () => void }) => {
  const { onClose, open } = props;
  const context = useContext(AppContext);

  const [username, setUsername] = useState<string>('');
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const handleChangeUsername = async () => {
    try {
      await userApi.userUpdateUsername({ changeUsernameDTO: { username: username } });
      setErrorMessages([]);
      context.refreshUserProfile();
      onClose();
    } catch (e) {
      if (e instanceof ResponseError) {
        const response = await e.response.json();
        const messages: Array<string> = response.message;
        setErrorMessages(messages);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">{'Choose a new callsign'}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">Choose your new username.</DialogContentText>
        <TextField
          error={errorMessages.length > 0}
          helperText={errorMessages[0]}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          required
          margin="dense"
          id="name"
          name="Username"
          label="Username"
          type="text"
          fullWidth
          variant="standard"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abort</Button>
        <Button onClick={handleChangeUsername} autoFocus>
          Change
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CancelSubscriptionDialog = (props: { open: boolean; onClose: () => void }) => {
  const { onClose, open } = props;
  const context = useContext(AppContext);

  const handleSubscriptionCancel = async () => {
    await stripeApi.stripeCancelSubscription({});
    context.refreshUserProfile();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">{'Really cancel your subscription?'}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Your premium subscription will be cancelled and end immediately. All premium content will be removed and you will be unable to create new exercises/sets if you exceed
          your exercise/set limit.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Abort
        </Button>
        <Button variant="outlined" color="error" onClick={handleSubscriptionCancel} autoFocus>
          Cancel subscription
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteAccountDialog = (props: { open: boolean; onClose: () => void }) => {
  const { onClose, open } = props;
  const navigate = useNavigate();

  const handleAccountDelete = async () => {
    await userApi.userDeleteUserAccount();
    await logout();
    onClose();
    navigate('/login');
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">{'Really delete your account?'}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Your data, including all sets and exercises, will be permanently deleted from the server. Are you sure you want to continue? This action is irreversible. Any active
          subscriptions will also be cancelled.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Disagree
        </Button>
        <Button variant="outlined" color="error" onClick={handleAccountDelete} autoFocus>
          Agree
        </Button>
      </DialogActions>
    </Dialog>
  );
};
